import { useState } from "react";
import { Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  Input,
  Select,
  Textarea,
} from "@/components/ui";
import { ApprovalPickerField } from "@/modules/approvals";
import type {
  AvailabilityType,
  BlackoutReasonType,
  MaintenanceAction,
  MaintenanceImpactType,
  PolicyAction,
  PolicyType,
} from "../types";
import { formatLabel, toIsoUtc } from "../labels";

const AVAILABILITY_TYPES: AvailabilityType[] = [
  "standard",
  "exception",
  "special_hours",
  "service_window",
  "appointment_window",
];

const BLACKOUT_REASONS: BlackoutReasonType[] = [
  "maintenance",
  "closure",
  "private_use",
  "safety",
  "policy_restriction",
  "operational_exception",
];

const POLICY_TYPES: PolicyType[] = [
  "pricing",
  "promotion",
  "visibility",
  "discovery",
  "future_governed_type",
];

const IMPACT_TYPES: MaintenanceImpactType[] = [
  "cancellation_risk",
  "cancellation_required",
  "rebooking_required",
  "refund_review",
  "customer_notification",
  "future_governed_type",
];

const POLICY_ACTIONS: PolicyAction[] = [
  "activate",
  "pause",
  "restrict",
  "archive",
  "retire",
];

const MAINT_ACTIONS: MaintenanceAction[] = [
  "schedule",
  "activate",
  "resolve",
  "cancel",
];

export function CreateAvailabilityDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    availability_type: AvailabilityType;
    starts_at: string;
    ends_at: string;
    recurrence_rule?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [availabilityType, setAvailabilityType] =
    useState<AvailabilityType>("standard");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [recurrence, setRecurrence] = useState("");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add availability window"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!startsAt || !endsAt}
            onClick={() =>
              void onSubmit({
                availability_type: availabilityType,
                starts_at: toIsoUtc(startsAt),
                ends_at: toIsoUtc(endsAt),
                ...(recurrence.trim()
                  ? { recurrence_rule: recurrence.trim() }
                  : {}),
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Windows are stored in UTC. Enter local time; HQ converts before send.
        </Text>
        <Select
          label="Type"
          value={availabilityType}
          onChange={(event) =>
            setAvailabilityType(event.target.value as AvailabilityType)
          }
          options={AVAILABILITY_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Starts"
          type="datetime-local"
          value={startsAt}
          onChange={(event) => setStartsAt(event.target.value)}
        />
        <Input
          label="Ends"
          type="datetime-local"
          value={endsAt}
          onChange={(event) => setEndsAt(event.target.value)}
        />
        <Input
          label="Recurrence rule (optional)"
          value={recurrence}
          onChange={(event) => setRecurrence(event.target.value)}
        />
        {error ? <Alert tone="danger" title="Create failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function CreateBlackoutDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    starts_at: string;
    ends_at: string;
    reason_type: BlackoutReasonType;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [reasonType, setReasonType] = useState<BlackoutReasonType>("closure");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add blackout"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!startsAt || !endsAt}
            onClick={() =>
              void onSubmit({
                starts_at: toIsoUtc(startsAt),
                ends_at: toIsoUtc(endsAt),
                reason_type: reasonType,
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Select
          label="Reason type"
          value={reasonType}
          onChange={(event) =>
            setReasonType(event.target.value as BlackoutReasonType)
          }
          options={BLACKOUT_REASONS.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Starts"
          type="datetime-local"
          value={startsAt}
          onChange={(event) => setStartsAt(event.target.value)}
        />
        <Input
          label="Ends"
          type="datetime-local"
          value={endsAt}
          onChange={(event) => setEndsAt(event.target.value)}
        />
        {error ? <Alert tone="danger" title="Create failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function CreateMaintenanceDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    starts_at: string;
    ends_at?: string | null;
    reason?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [reason, setReason] = useState("");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add maintenance event"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!startsAt}
            onClick={() =>
              void onSubmit({
                starts_at: toIsoUtc(startsAt),
                ...(endsAt ? { ends_at: toIsoUtc(endsAt) } : {}),
                ...(reason.trim() ? { reason: reason.trim() } : {}),
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Input
          label="Starts"
          type="datetime-local"
          value={startsAt}
          onChange={(event) => setStartsAt(event.target.value)}
        />
        <Input
          label="Ends (optional)"
          type="datetime-local"
          value={endsAt}
          onChange={(event) => setEndsAt(event.target.value)}
        />
        <Textarea
          label="Reason (optional)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? <Alert tone="danger" title="Create failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function MaintenanceActionDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    action: MaintenanceAction;
    reason?: string;
    ends_at?: string | null;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [action, setAction] = useState<MaintenanceAction>("schedule");
  const [reason, setReason] = useState("");
  const [endsAt, setEndsAt] = useState("");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Maintenance action"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() =>
              void onSubmit({
                action,
                ...(reason.trim() ? { reason: reason.trim() } : {}),
                ...(endsAt ? { ends_at: toIsoUtc(endsAt) } : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Select
          label="Action"
          value={action}
          onChange={(event) =>
            setAction(event.target.value as MaintenanceAction)
          }
          options={MAINT_ACTIONS.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Input
          label="Ends at (optional)"
          type="datetime-local"
          value={endsAt}
          onChange={(event) => setEndsAt(event.target.value)}
        />
        <Textarea
          label="Reason (optional)"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        {error ? <Alert tone="danger" title="Action failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function CreateImpactDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    booking_id: string;
    impact_type: MaintenanceImpactType;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [bookingId, setBookingId] = useState("");
  const [impactType, setImpactType] =
    useState<MaintenanceImpactType>("customer_notification");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Record booking impact"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={bookingId.trim().length === 0}
            onClick={() =>
              void onSubmit({
                booking_id: bookingId.trim(),
                impact_type: impactType,
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Impacts link an existing Booking to this maintenance event. Booking
          lifecycle stays in the Bookings module.
        </Text>
        <Input
          label="Booking ID"
          value={bookingId}
          onChange={(event) => setBookingId(event.target.value)}
        />
        <Select
          label="Impact type"
          value={impactType}
          onChange={(event) =>
            setImpactType(event.target.value as MaintenanceImpactType)
          }
          options={IMPACT_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        {error ? <Alert tone="danger" title="Create failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function CreatePolicyDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    policy_type: PolicyType;
    is_exception?: boolean;
    approval_id?: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [policyType, setPolicyType] = useState<PolicyType>("visibility");
  const [isException, setIsException] = useState(false);
  const [approvalId, setApprovalId] = useState("");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add pricing / visibility policy"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={() =>
              void onSubmit({
                policy_type: policyType,
                ...(isException ? { is_exception: true } : {}),
                ...(approvalId.trim() ? { approval_id: approvalId.trim() } : {}),
              })
            }
          >
            Create
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Text role="small" color="secondary" as="p">
          Exception or high-impact policies may require an approved Approval.
          The backend remains authoritative.
        </Text>
        <Select
          label="Policy type"
          value={policyType}
          onChange={(event) => setPolicyType(event.target.value as PolicyType)}
          options={POLICY_TYPES.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Checkbox
          label="Mark as exception"
          checked={isException}
          onChange={(event) => setIsException(event.target.checked)}
        />
        <ApprovalPickerField
          label="Approved Approval (when required)"
          value={approvalId}
          onChange={setApprovalId}
        />
        {error ? <Alert tone="danger" title="Create failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}

export function PolicyActionDialog({
  open,
  onClose,
  onSubmit,
  busy = false,
  error = null,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    action: PolicyAction;
    reason: string;
    is_exception?: boolean;
    is_high_impact?: boolean;
    approval_id?: string;
  }) => Promise<void>;
  busy?: boolean;
  error?: string | null;
}) {
  const [action, setAction] = useState<PolicyAction>("activate");
  const [reason, setReason] = useState("");
  const [isException, setIsException] = useState(false);
  const [isHighImpact, setIsHighImpact] = useState(false);
  const [approvalId, setApprovalId] = useState("");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Policy action"
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant={action === "retire" || action === "restrict" ? "danger" : "primary"}
            loading={busy}
            disabled={reason.trim().length < 3}
            onClick={() =>
              void onSubmit({
                action,
                reason,
                ...(isException ? { is_exception: true } : {}),
                ...(isHighImpact ? { is_high_impact: true } : {}),
                ...(approvalId.trim() ? { approval_id: approvalId.trim() } : {}),
              })
            }
          >
            Confirm {formatLabel(action)}
          </Button>
        </>
      }
    >
      <Stack gap={16}>
        <Select
          label="Action"
          value={action}
          onChange={(event) => setAction(event.target.value as PolicyAction)}
          options={POLICY_ACTIONS.map((item) => ({
            value: item,
            label: formatLabel(item),
          }))}
        />
        <Textarea
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
        <Checkbox
          label="Exception"
          checked={isException}
          onChange={(event) => setIsException(event.target.checked)}
        />
        <Checkbox
          label="High impact"
          checked={isHighImpact}
          onChange={(event) => setIsHighImpact(event.target.checked)}
        />
        <ApprovalPickerField
          label="Approved Approval (when required)"
          value={approvalId}
          onChange={setApprovalId}
        />
        {error ? <Alert tone="danger" title="Action failed">{error}</Alert> : null}
      </Stack>
    </Dialog>
  );
}
